<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { FilterModal, DatePicker, IconImage, ThemeProvider } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { QualityRecordStatus } from '@/entities/Document';
  import type { FilterOptions } from '@/entities/Document';
  import { QualityRecordStatusLabels } from '@/pages/home/config';
  import { STAGE_OPTIONS } from '@/entities/Document';
  import { parseString, parseDate } from '@/shared/lib';

  const props = defineProps<{
    currentFilters?: FilterOptions;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'clear-filters'): void;
    (e: 'apply-filters'): void;
    (e: 'update:filter', filters: FilterOptions): void;
  }>();

  const onSubmit = (e: Event) => {
    e.preventDefault();
  };

  const formStatus = ref<string>('');
  const formStage = ref<string>('');
  const createdAt = ref<Date | null>(null);
  const completedAt = ref<Date | null>(null);

  const isCreatedOpen = ref(false);
  const isCompletedOpen = ref(false);

  // Create options from enum
  const statusOptions = computed(() =>
    Object.entries(QualityRecordStatus).map(([key, value]) => ({
      id: value,
      name: QualityRecordStatusLabels[value as QualityRecordStatus],
    }))
  );

  function loadFilters(filters?: FilterOptions) {
    if (!filters) return;

    formStatus.value = parseString(filters.status);
    formStage.value = parseString(filters.stage);
    createdAt.value = parseDate(filters.createdAt);
    completedAt.value = parseDate(filters.completedAt);
  }

  onMounted(() => {
    loadFilters(props.currentFilters);
  });

  watch(
    () => props.currentFilters,
    (newFilters?: FilterOptions) => {
      if (newFilters) {
        loadFilters(newFilters);
      }
    },
    { deep: true }
  );

  function clearFilters() {
    formStatus.value = '';
    formStage.value = '';
    createdAt.value = null;
    completedAt.value = null;

    emit('clear-filters');
  }

  function applyFilters() {
    const filters: any = {
      status: formStatus.value || '',
      stage: formStage.value || '',
      createdAt: createdAt.value,
      completedAt: completedAt.value,
    };

    const hasActiveFilters = Object.values(filters).some(
      (value) => value !== null && value !== undefined && value !== ''
    );

    if (!hasActiveFilters) {
      clearFilters();
      emit('update:filter', {});
    } else {
      emit('update:filter', filters);
    }
  }

  function formatButtonDate(d: Date | null): string {
    if (!d) return 'Selecione uma data';
    return d.toLocaleDateString('pt-BR');
  }
</script>

<template>
  <FilterModal
    @close="emit('close')"
    @clear-filters="clearFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <form @submit.prevent="onSubmit">
        <ThemeProvider>
          <div class="px-6 space-y-4">
            <div class="flex gap-4 py-8 px-4">
              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 mb-3">
                  Status
                </h4>
                <div class="flex flex-col gap-2 pl-2">
                  <button
                    v-for="option in statusOptions"
                    :key="option.id"
                    :class="[
                      'py-2 px-3 rounded-lg w-auto self-start text-left',
                      formStatus === option.id
                        ? 'bg-(--theme-secondary) text-white'
                        : 'text-gray-07',
                    ]"
                    type="button"
                    @click="formStatus = formStatus === option.id ? '' : option.id"
                  >
                    {{ option.name }}
                  </button>
                </div>
              </div>

              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 mb-3">
                  Etapa
                </h4>
                <div class="flex flex-col gap-2 pl-2">
                  <button
                    v-for="option in STAGE_OPTIONS"
                    :key="option.id"
                    :class="[
                      'py-2 px-3 rounded-lg w-auto self-start text-left',
                      formStage === option.id
                        ? 'bg-(--theme-secondary) text-white'
                        : 'text-gray-07',
                    ]"
                    type="button"
                    @click="formStage = formStage === option.id ? '' : option.id"
                  >
                    {{ option.name }}
                  </button>
                </div>
              </div>
            </div>

            <div class="flex py-12 px-4 gap-4 border-y border-gray-02">
              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 pb-4">
                  Data de Criação
                </h4>
                <button
                  class="flex items-center bg-white-01 px-8 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium w-full"
                  type="button"
                  @click="isCreatedOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-5 w-5"
                  />
                  <span class="grow text-center">
                    {{ formatButtonDate(createdAt) }}
                  </span>
                </button>
                <DatePicker
                  v-model:selected-date="createdAt"
                  v-model:is-open="isCreatedOpen"
                  class="my-auto"
                  @apply="isCreatedOpen = false"
                  @cancel="isCreatedOpen = false"
                />
              </div>

              <div class="w-1/2">
                <h4 class="font-medium text-gray-07 pb-4">
                  Data de Conclusão
                </h4>
                <button
                  class="flex items-center bg-white-01 px-8 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium w-full"
                  type="button"
                  @click="isCompletedOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-5 w-5"
                  />
                  <span class="grow text-center">
                    {{ formatButtonDate(completedAt) }}
                  </span>
                </button>
                <DatePicker
                  v-model:selected-date="completedAt"
                  v-model:is-open="isCompletedOpen"
                  class="my-auto"
                  @apply="isCompletedOpen = false"
                  @cancel="isCompletedOpen = false"
                />
              </div>
            </div>
          </div>
        </ThemeProvider>
      </form>
    </template>
  </FilterModal>
</template>
